import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import type { ApiClient } from '@shared/api';
import { ApiError } from '@shared/api';
import type { Session } from '@shared/auth';
import { connectHub, type ConnectionStatus } from '@shared/realtime';

interface AppCtx {
  api: ApiClient;
  session: Session;
  online: boolean;
  hub: HubConnection | null;
  hubStatus: ConnectionStatus;
  toast: (msg: string, kind?: 'ok' | 'error') => void;
}
const Ctx = createContext<AppCtx | null>(null);
export const useApp = () => useContext(Ctx)!;

export function AppProvider({ api, session, children }: { api: ApiClient; session: Session; children: ReactNode }) {
  const online = useOnline();
  const [hub, setHub] = useState<HubConnection | null>(null);
  const [hubStatus, setHubStatus] = useState<ConnectionStatus>('connecting');
  const [toasts, setToasts] = useState<{ id: number; msg: string; kind: 'ok' | 'error' }[]>([]);
  const nextId = useRef(1);

  useEffect(() => {
    const c = connectHub({ session, onStatus: setHubStatus, onConnected: (h) => setHub(h) });
    setHub(c.hub);
    return () => { void c.stop(); };
  }, [session]);

  const toast = useCallback((msg: string, kind: 'ok' | 'error' = 'ok') => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 7000 : 3500);
  }, []);

  return (
    <Ctx.Provider value={{ api, session, online, hub, hubStatus, toast }}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`toast toast-${t.kind}`}>{t.msg}</div>)}
      </div>
    </Ctx.Provider>
  );
}

/** Browser online flag plus an active probe, so "online" means the API is actually reachable. */
function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return online;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const errs = e.problem.errors ? Object.values(e.problem.errors).flat().join(' ') : '';
    return errs || e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/** Small data-loading hook with reload. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | undefined; error: string | null; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    fn().then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e) => { if (live) setError(errorMessage(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { data, error, loading, reload: () => setN((x) => x + 1) };
}
